import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignCategoryRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignCategories']

type UseCampaignCategoriesOptions = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignCategoryRequestOptions,
}

function useCampaignCategories(options: UseCampaignCategoriesOptions) {
  const { performHttpRequests = false, requestOptions } = options

  const {
    updatedEntities: { campaignCategories: campaignCategoriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignCategories } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignCategories = useMemo(
    () => sortArrayBy(Object.values(campaignCategories), 'asc', 'title'),
    [campaignCategoriesUpdatedAt],
  )

  const filteredCampaignCategoriesCount = filteredCampaignCategories.length
  const hasFilteredCampaignCategories = !!filteredCampaignCategoriesCount

  const { loading: loadingCampaignCategories } = useReduxAction(
    'campaignCategories',
    'loadCampaignCategories',
    {
      ...requestOptions,
    },
    [performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    filteredCampaignCategories,
    filteredCampaignCategoriesCount,
    hasFilteredCampaignCategories,
    loading: loadingCampaignCategories,
  }
}

export default useCampaignCategories
